import { FC, useCallback, useEffect, useState } from "react";
import {
  Typography,
  Box,
  useMediaQuery,
  Link,
  IconButton,
} from "@mui/material";
import BackgroundLayout from "../../components/custom-backgrounds/BackgroundLayout";
import styles from "./WelcomeOverviewFinalStep.module.scss";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { useSaveDashoboardPreferenceMutation } from "./services/SaveDashboardPreferenceApiSlice";
import { useAppSelector } from "../../store/hooks";
import { useGetDragDropListQuery } from "../../components/custom-drag-drop/services/GetDragDropListSlice";
import CustomDragDdrop from "../../components/custom-drag-drop/CustomDragDrop";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import update from "immutability-helper";
import { DragDropCardList } from "../../utility/interfaces/carePlanInterfaces";
import DraggableItemSkeleton from "../../components/custom-drag-drop/CustomDragDropSkeleton";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";

interface FinalMessage {
  title: string;
  content: string;
}

interface WelcomeOverviewFinalStepProps {
  finalMessage: FinalMessage;
  onClose: () => void;
  showBackArrow: boolean;
  handleBack: () => void;
}

const WelcomeOverviewFinalStep: FC<WelcomeOverviewFinalStepProps> = ({
  finalMessage,
  onClose,
  showBackArrow,
  handleBack,
}) => {
  const { t } = useTranslation();
  const patientProfileId = useAppSelector(
    (state) => state.patientOnboardingStatus.patientProfileId
  );
  const preferredLanguage = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );
  const {
    data: dragDropList,
    error: dragDropError,
    isLoading: dragDropLoading,
  } = useGetDragDropListQuery({
    patientId: patientProfileId,
    preferredLanguage,
  });

  const [cards, setCards] = useState<DragDropCardList[]>([]);
  const [saveDashoboardPreference] = useSaveDashoboardPreferenceMutation();

  useEffect(() => {
    if (dragDropList?.appModules?.length) {
      setCards(dragDropList.appModules);
    }
  }, [dragDropList]);

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setCards((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);

  const handleCompleteClick = async () => {
    const body = cards.map((card, index) => ({
      appModuleId: card.appModuleId,
      displaySequence: index + 1, // 1-based order
    }));

    try {
      const response = await saveDashoboardPreference({
        body,
        patientProfileId,
        preferredLanguage,
      }).unwrap();
      enqueueSnackbar(response.apiResponse, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
    }
    onClose();
  };

  return (
    <Box
      className={styles.container}
    >
      <Typography
        variant="caption"
        className={styles.caption}
      >
        {t("lastStep")}
      </Typography>

      <Typography
        variant="h1"
        className={styles.title}
      >
        {finalMessage.title}
      </Typography>

      <Typography
        variant="body1"
        className={styles.subtitle}
        mt={{ xs: "5.87rem", sm: "5.87rem", md: "7.69rem", lg: "15rem" }}
        dangerouslySetInnerHTML={{ __html: finalMessage.content }}
      />

      <Box className={styles.listPlaceholder}>
        <DataLoader
          isLoading={dragDropLoading}
          error={dragDropError}
          data={dragDropList?.appModules}
          loadingComponent={<DraggableItemSkeleton />}
          emptyComponent={<Typography>No preferences found.</Typography>}
          errorComponent={
            <Typography color="error">
              Error loading dashboard personalization data.
            </Typography>
          }
        >
          {() => (
            <CustomDragDdrop
              cards={cards}
              onMoveCard={moveCard}
            />
          )}
        </DataLoader>
      </Box>
      <Box className={styles.actionsWrapper}>
        {showBackArrow && (
          <IconButton onClick={handleBack}>
            <ArrowIcon
              width={24}
              height={24}
              fill="#1A1A1A"
            />
          </IconButton>
        )}

        <Box className={styles.buttonActions}>
          <CustomButton
            sx={{ fontSize: "1rem", width: "134px" }}
            variant="contained"
            text={t("completedBtn")}
            onClick={handleCompleteClick}
          />
          <Link
            onClick={onClose}
            className={styles.skipLink}
            color="primary"
          >
            {t("skipForNow")}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default WelcomeOverviewFinalStep;
